import { useEffect, useRef } from 'react';
import type { ProjectRole, ProposalSummary } from '@integrate/contracts';
import type { AgentTurn } from '@integrate/contracts';
import type { SeparationState } from '@integrate/core';
import { AssistantChanges } from './AssistantChanges.js';

/**
 * The conversation, oldest first.
 *
 * ONE BLOCK PER TURN, and each block renders the same three outcomes the single answer always did — a
 * proposal, words, or nothing — because a transcript that showed only the proposals would be blank for
 * every question answered in a sentence, which is most of them.
 *
 * THE TESTID RULE, WHICH IS A RULE WITH A REASON RATHER THAN A WORKAROUND.
 *
 * `test-support.tsx`'s `byId` is `querySelector` — the FIRST match in document order. Before this file
 * there was exactly one answer on screen, so `assistant-proposal` was unambiguous; thirteen assertions
 * across two suites and the end-to-end run read it that way. Put the same id on every turn in a
 * transcript rendered oldest-first and all thirteen would silently start reading the OLDEST answer:
 * still passing, still green, and testing the wrong thing.
 *
 * So the NEWEST turn carries the bare ids and older turns carry `assistant-turn-<n>-*`. That is not a
 * concession to the helper. The bare ids have always meant "what the assistant just said", which is
 * what every one of those assertions is about; the transcript is the new thing and it is the one that
 * gets the qualified names.
 */
export function AssistantThread({
  turns,
  scenarioId,
  projectId,
  state,
  onOpenProposals,
  role,
  myUserId,
  onProposalSettled,
}: {
  /** Oldest first, as the server returns them. */
  turns: readonly AgentTurn[];
  /**
   * The scenario the reader is standing in, so a turn asked somewhere else can say so.
   *
   * Not cosmetic: `agent.ts` filters the feed to the model by scenario, so a turn from another design
   * is in the transcript but is NOT an antecedent for the next question. A reader who cannot see that
   * would reasonably write "and now do the same to them" expecting it to resolve.
   */
  scenarioId: string | null;
  projectId: string;
  /** The design, for turning ids into names in the rendered changes. Null while it loads. */
  state: SeparationState | null;
  onOpenProposals(): void;
  /** Whose buttons to draw under a proposal. The server decides; this decides what to offer. */
  role: ProjectRole | null;
  myUserId: string | null;
  onProposalSettled(turnId: string, proposal: ProposalSummary): void;
}): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);

  /*
    THE BOTTOM IS WHERE THE CONVERSATION IS. A transcript that opened at the top would show somebody
    the question they asked on Tuesday and hide the answer that just arrived.

    `turns.length` rather than `turns` — the array identity changes on every parent render, and
    scrolling on each of those would fight a reader who has deliberately scrolled up to re-read
    something while the panel re-renders around them.
  */
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [turns.length]);

  return (
    <div className="assistant-thread" data-testid="assistant-thread">
      {turns.map((turn, i) => {
        // See the header: the last turn is "what the assistant just said" and keeps the bare ids.
        const newest = i === turns.length - 1;
        const id = (suffix: string): string =>
          newest ? `assistant-${suffix}` : `assistant-turn-${i}-${suffix}`;

        return (
          <div className="assistant-turn" key={turn.id} data-testid={id('turn')}>
            {/*
              `asked`, NOT `prompt`. The composer's textarea already owns `assistant-prompt`, and
              `byId` is `querySelector` — the first match in document order. The transcript renders
              ABOVE the composer, so naming this one `assistant-prompt` would have every existing
              assertion about "the box" silently start reading a paragraph instead. It is the same
              first-match hazard the header describes, arriving from the other direction.
            */}
            <p className="assistant-said" data-testid={id('asked')}>
              {turn.prompt}
            </p>

            <div data-testid={id('answer')}>
              {turn.proposal && (
                <p data-testid={id('proposal')}>
                  Raised a proposal: <strong>{turn.proposal.title}</strong> ·{' '}
                  {turn.proposal.commandCount}{' '}
                  {turn.proposal.commandCount === 1 ? 'change' : 'changes'}.{' '}
                  {/* Nothing has moved yet, and the sentence says so before offering the way in. */}
                  Nothing has changed until somebody applies it.{' '}
                  <button className="linkish" data-testid={id('open-proposals')} onClick={onOpenProposals}>
                    Review it
                  </button>
                </p>
              )}

              {/*
                WHAT IT WILL ACTUALLY DO, on the newest turn only — see `AssistantChanges` for why the
                pair of statements matters and why older turns keep the link instead.
              */}
              {turn.proposal && newest && (
                <AssistantChanges
                  projectId={projectId}
                  proposal={turn.proposal}
                  state={state}
                  role={role}
                  myUserId={myUserId}
                  onSettled={(proposal) => onProposalSettled(turn.id, proposal)}
                />
              )}

              {turn.text && (
                <p className="muted" data-testid={id('text')}>
                  “{turn.text}”
                </p>
              )}

              {!turn.proposal && !turn.text && (
                <p className="muted" data-testid={id('nothing')}>
                  The assistant had nothing to suggest for that.
                </p>
              )}

              {turn.rejected.length > 0 && (
                /*
                  Not an error state — the rest of the turn may be perfectly good. It is a list of what
                  the assistant asked for and did not get, so the reader knows the answer beside it is
                  narrower than what they asked for.
                */
                <div data-testid={id('rejected')}>
                  <p className="muted small">Not included:</p>
                  <ul className="hlist">
                    {turn.rejected.map((r, j) => (
                      <li
                        key={`${r.name}-${j}`}
                        data-testid={newest ? `assistant-rejected-${j}` : `assistant-turn-${i}-rejected-${j}`}
                      >
                        <code>{r.name}</code> — {r.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!turn.sawRoster && (
                /*
                  The honest caveat, and the reason `sawRoster` is stored per turn rather than per
                  project. Over `MAX_CONTEXT_PEOPLE` the model is given the organisation's SHAPE and
                  nobody in it, so "I could not identify those people" is the truth rather than a
                  malfunction. A roster that grew past the ceiling between two questions makes the later
                  one weaker, and the transcript should show which — which a project-level flag could
                  not do.
                */
                <p className="muted small" data-testid={id('shape-only')}>
                  This project is too large to describe person by person, so the assistant was given the
                  organisation’s shape rather than its people. It cannot name individuals.
                </p>
              )}

              {turn.scenarioId !== scenarioId && (
                /*
                  SHOWN, NOT HIDDEN, and this label is the reason that is safe. The thread is per person
                  per project and crosses scenarios on purpose — "what if we moved Finance instead" is
                  precisely the sentence that crosses — but the feed to the model does not. Without the
                  label a reader would see the exchange, write "now do the same to them", and get an
                  answer with no antecedent. A silent gap would be worse still: they would not even know
                  what the assistant was not told.
                */
                <p className="muted small" data-testid={id('elsewhere')}>
                  Asked in another scenario, so it is not carried into new questions here.
                </p>
              )}
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}
